import { LocalDate } from '@js-joda/core';
import type { Pour, StopeBackfillSummaryDto, StopeDataDto } from 'providers/api';
import { formatEpochDay, toEpochDay } from '../../MineModel/Meshes/stopeShapeDateRange';

export type StopeLegendBinMode = 'range' | 'value';
export type StopeLegendPropertyKind = 'number' | 'date' | 'category';

export interface StopeLegendBin {
  id: string;
  label: string;
  color: string;
  min?: number;
  max?: number;
  value?: string | number;
}

export interface StopeLegendConfig {
  id: string;
  name: string;
  propertyId: string;
  binMode: StopeLegendBinMode;
  bins: StopeLegendBin[];
  fallbackColor?: string;
}

export interface SavedStopeLegends {
  activeLegendId?: string | null;
  legends: StopeLegendConfig[];
}

export interface StopeLegendSource {
  stopeId: string;
  stopeName?: string;
  volume?: number;
  startDate?: unknown;
  endDate?: unknown;
  pours?: Pour[];
  totalCount?: number;
  plannedCount?: number;
  completedCount?: number;
  plannedVolume?: number;
  completedVolume?: number;
  isComplete?: boolean;
}

interface StopeLegendPropertyOption {
  id: string;
  label: string;
  kind: StopeLegendPropertyKind;
  defaultBinMode: StopeLegendBinMode;
  getValue: (stope: StopeLegendSource) => string | number | null;
  formatValue: (value: string | number) => string;
}

const DEFAULT_COLORS = [
  '#5f2eea',
  '#2f80ed',
  '#27ae60',
  '#f2c94c',
  '#f2994a',
  '#eb5757',
  '#c92a2a',
  '#7a1f8a',
  '#0f766e',
  '#8a5a00',
];

const STORAGE_VERSION = 1;
const FALLBACK_COLOR = '#8a8f98';

const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const weekdayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const makeId = () => `legend-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const roundNumber = (value: number) => Math.round(value);
const roundValue = (value: string | number): string | number => (typeof value === 'number' ? roundNumber(value) : value);

const average = (values: Array<number | undefined | null>): number | null => {
  const finiteValues = values.filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
  if (finiteValues.length === 0) return null;
  return finiteValues.reduce((sum, value) => sum + value, 0) / finiteValues.length;
};

const getPours = (stope: StopeLegendSource): Pour[] => (stope.pours ?? []).filter((pour) => !pour.isDeleted);

const getAverageUcs = (stope: StopeLegendSource): number | null => average(getPours(stope).map((pour) => (
  pour.pourData?.ucsAverage
  ?? pour.pasteSelectedSpecification?.ucsStrength
  ?? pour.pasteOptimisedSpecification?.ucsStrength
  ?? pour.pasteSpecification?.targetUCSStrength
)));

const getAverageBinder = (stope: StopeLegendSource): number | null => average(getPours(stope).map((pour) => (
  pour.pourData?.binderContent
  ?? pour.pasteSelectedSpecification?.binderContent
  ?? pour.pasteOptimisedSpecification?.binderContent
)));

const getAverageMassConcentration = (stope: StopeLegendSource): number | null => average(getPours(stope).map((pour) => (
  pour.pourData?.massConcentration
  ?? pour.pasteSelectedSpecification?.massConcentration
  ?? pour.hydraulicSelectedSpecification?.massConcentration
  ?? pour.pasteOptimisedSpecification?.massConcentration
  ?? pour.hydraulicOptimisedSpecification?.massConcentration
)));

const getLocalDate = (value: unknown): LocalDate | null => {
  const epochDay = toEpochDay(value);
  if (epochDay === null) return null;
  return LocalDate.ofEpochDay(epochDay);
};

const getEpochDay = (value: unknown): number | null => toEpochDay(value);

const formatNumber = (value: string | number): string => {
  const numericValue = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numericValue)) return `${value}`;
  if (Math.abs(numericValue) >= 100) return numericValue.toFixed(0);
  if (Math.abs(numericValue) >= 10) return numericValue.toFixed(1);
  return numericValue.toFixed(2).replace(/\.?0+$/, '');
};

const formatPercent = (value: string | number): string => `${formatNumber(value)}%`;
const formatDate = (value: string | number): string => {
  const numericValue = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numericValue) ? formatEpochDay(numericValue) : `${value}`;
};
const formatMonth = (value: string | number): string => {
  const month = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(month) && month >= 1 && month <= 12 ? monthNames[month - 1] : `${value}`;
};
const formatWeekday = (value: string | number): string => {
  const weekday = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(weekday) && weekday >= 1 && weekday <= 7 ? weekdayNames[weekday - 1] : `${value}`;
};

export const STOPE_LEGEND_PROPERTY_OPTIONS: StopeLegendPropertyOption[] = [
  {
    id: 'volume',
    label: 'Volume',
    kind: 'number',
    defaultBinMode: 'range',
    getValue: (stope) => stope.volume ?? null,
    formatValue: formatNumber,
  },
  {
    id: 'plannedVolume',
    label: 'Planned volume',
    kind: 'number',
    defaultBinMode: 'range',
    getValue: (stope) => stope.plannedVolume ?? null,
    formatValue: formatNumber,
  },
  {
    id: 'completedVolume',
    label: 'Completed volume',
    kind: 'number',
    defaultBinMode: 'range',
    getValue: (stope) => stope.completedVolume ?? null,
    formatValue: formatNumber,
  },
  {
    id: 'fillPercent',
    label: 'Fill percent',
    kind: 'number',
    defaultBinMode: 'range',
    getValue: (stope) => {
      const volume = stope.volume ?? 0;
      if (volume <= 0) return null;
      return ((stope.completedVolume ?? 0) / volume) * 100;
    },
    formatValue: formatPercent,
  },
  {
    id: 'totalPours',
    label: 'Number of pours',
    kind: 'number',
    defaultBinMode: 'value',
    getValue: (stope) => stope.totalCount ?? getPours(stope).length,
    formatValue: formatNumber,
  },
  {
    id: 'plannedPours',
    label: 'Planned pours',
    kind: 'number',
    defaultBinMode: 'value',
    getValue: (stope) => stope.plannedCount ?? null,
    formatValue: formatNumber,
  },
  {
    id: 'completedPours',
    label: 'Completed pours',
    kind: 'number',
    defaultBinMode: 'value',
    getValue: (stope) => stope.completedCount ?? null,
    formatValue: formatNumber,
  },
  {
    id: 'averageUcs',
    label: 'Average strength',
    kind: 'number',
    defaultBinMode: 'range',
    getValue: getAverageUcs,
    formatValue: formatNumber,
  },
  {
    id: 'averageBinder',
    label: 'Average binder %',
    kind: 'number',
    defaultBinMode: 'range',
    getValue: getAverageBinder,
    formatValue: formatPercent,
  },
  {
    id: 'averageMassConcentration',
    label: 'Average mass conc %',
    kind: 'number',
    defaultBinMode: 'range',
    getValue: getAverageMassConcentration,
    formatValue: formatPercent,
  },
  {
    id: 'startDate',
    label: 'Start date',
    kind: 'date',
    defaultBinMode: 'range',
    getValue: (stope) => getEpochDay(stope.startDate),
    formatValue: formatDate,
  },
  {
    id: 'startYear',
    label: 'Start year',
    kind: 'category',
    defaultBinMode: 'value',
    getValue: (stope) => getLocalDate(stope.startDate)?.year() ?? null,
    formatValue: formatNumber,
  },
  {
    id: 'startMonth',
    label: 'Start month',
    kind: 'category',
    defaultBinMode: 'value',
    getValue: (stope) => getLocalDate(stope.startDate)?.monthValue() ?? null,
    formatValue: formatMonth,
  },
  {
    id: 'startWeekday',
    label: 'Start weekday',
    kind: 'category',
    defaultBinMode: 'value',
    getValue: (stope) => getLocalDate(stope.startDate)?.dayOfWeek().value() ?? null,
    formatValue: formatWeekday,
  },
  {
    id: 'endDate',
    label: 'End date',
    kind: 'date',
    defaultBinMode: 'range',
    getValue: (stope) => getEpochDay(stope.endDate),
    formatValue: formatDate,
  },
  {
    id: 'endYear',
    label: 'End year',
    kind: 'category',
    defaultBinMode: 'value',
    getValue: (stope) => getLocalDate(stope.endDate)?.year() ?? null,
    formatValue: formatNumber,
  },
  {
    id: 'endMonth',
    label: 'End month',
    kind: 'category',
    defaultBinMode: 'value',
    getValue: (stope) => getLocalDate(stope.endDate)?.monthValue() ?? null,
    formatValue: formatMonth,
  },
  {
    id: 'endWeekday',
    label: 'End weekday',
    kind: 'category',
    defaultBinMode: 'value',
    getValue: (stope) => getLocalDate(stope.endDate)?.dayOfWeek().value() ?? null,
    formatValue: formatWeekday,
  },
];

export const getStopeLegendPropertyOption = (propertyId: string) => (
  STOPE_LEGEND_PROPERTY_OPTIONS.find((option) => option.id === propertyId) ?? STOPE_LEGEND_PROPERTY_OPTIONS[0]
);

export const getStopeLegendStorageKey = (projectId: string) => `${projectId}-stopeLegendBuilder-v${STORAGE_VERSION}`;

export const loadSavedStopeLegends = (projectId: string | undefined): SavedStopeLegends => {
  if (!projectId) return { activeLegendId: null, legends: [] };

  try {
    const raw = localStorage.getItem(getStopeLegendStorageKey(projectId));
    if (!raw) return { activeLegendId: null, legends: [] };
    const parsed = JSON.parse(raw) as SavedStopeLegends;
    return {
      activeLegendId: parsed.activeLegendId ?? null,
      legends: Array.isArray(parsed.legends) ? parsed.legends : [],
    };
  } catch {
    return { activeLegendId: null, legends: [] };
  }
};

export const saveSavedStopeLegends = (projectId: string, saved: SavedStopeLegends) => {
  localStorage.setItem(getStopeLegendStorageKey(projectId), JSON.stringify(saved));
};

export const getActiveStopeLegendConfig = (projectId: string | undefined): StopeLegendConfig | null => {
  const saved = loadSavedStopeLegends(projectId);
  return saved.legends.find((legend) => legend.id === saved.activeLegendId) ?? null;
};

export const setActiveStopeLegendConfig = (projectId: string, legend: StopeLegendConfig | null) => {
  const saved = loadSavedStopeLegends(projectId);
  saveSavedStopeLegends(projectId, {
    activeLegendId: legend?.id ?? null,
    legends: legend
      ? [
        ...saved.legends.filter((item) => item.id !== legend.id),
        legend,
      ]
      : saved.legends,
  });
};

const sortValues = (values: Array<string | number>) => [...values].sort((a, b) => {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return `${a}`.localeCompare(`${b}`, undefined, { numeric: true });
});

export const buildStopeLegendSource = (
  stopeData: StopeDataDto,
  overview?: StopeBackfillSummaryDto,
): StopeLegendSource => ({
  stopeId: stopeData.stopeId,
  stopeName: stopeData.stopeName,
  volume: stopeData.volume,
  startDate: stopeData.startDate,
  endDate: stopeData.endDate,
  pours: stopeData.pour ?? [],
  totalCount: overview?.totalCount,
  plannedCount: overview?.plannedCount,
  completedCount: overview?.completedCount,
  plannedVolume: overview?.plannedVolume,
  completedVolume: overview?.completedVolume,
  isComplete: overview?.isComplete,
});

export const buildDefaultStopeLegendConfig = (
  stopes: StopeLegendSource[],
  propertyId: string,
  binMode?: StopeLegendBinMode,
  binCount = 7,
  existingConfig?: StopeLegendConfig | null,
): StopeLegendConfig => {
  const property = getStopeLegendPropertyOption(propertyId);
  const resolvedBinMode = binMode ?? property.defaultBinMode;
  const values = stopes
    .map((stope) => property.getValue(stope))
    .filter((value): value is string | number => value !== null && value !== undefined && `${value}` !== '');

  const name = existingConfig?.name?.trim() || property.label;
  const id = existingConfig?.id ?? makeId();

  if (resolvedBinMode === 'value' || property.kind === 'category') {
    const uniqueValues = sortValues(Array.from(new Set(values.map(roundValue))));
    return {
      id,
      name,
      propertyId,
      binMode: 'value',
      fallbackColor: existingConfig?.fallbackColor ?? FALLBACK_COLOR,
      bins: uniqueValues.map((value, index) => ({
        id: makeId(),
        value,
        label: property.formatValue(value),
        color: DEFAULT_COLORS[index % DEFAULT_COLORS.length],
      })),
    };
  }

  const numericValues = values
    .map((value) => (typeof value === 'number' ? value : Number(value)))
    .filter((value) => Number.isFinite(value));
  const min = Math.min(...numericValues);
  const max = Math.max(...numericValues);

  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return {
      id,
      name,
      propertyId,
      binMode: 'range',
      fallbackColor: existingConfig?.fallbackColor ?? FALLBACK_COLOR,
      bins: [],
    };
  }

  if (min === max) {
    return {
      id,
      name,
      propertyId,
      binMode: 'range',
      fallbackColor: existingConfig?.fallbackColor ?? FALLBACK_COLOR,
      bins: [{
        id: makeId(),
        min: roundNumber(min),
        max: roundNumber(max),
        label: property.formatValue(min),
        color: DEFAULT_COLORS[0],
      }],
    };
  }

  const count = Math.max(2, Math.min(20, Math.round(binCount)));
  const step = (max - min) / count;

  return {
    id,
    name,
    propertyId,
    binMode: 'range',
    fallbackColor: existingConfig?.fallbackColor ?? FALLBACK_COLOR,
    bins: Array.from({ length: count }, (_, index) => {
      const from = roundNumber(min + (step * index));
      const to = roundNumber(index === count - 1 ? max : min + (step * (index + 1)));
      return {
        id: makeId(),
        min: from,
        max: to,
        label: `${property.formatValue(from)} - ${property.formatValue(to)}`,
        color: DEFAULT_COLORS[index % DEFAULT_COLORS.length],
      };
    }),
  };
};

export const getStopeLegendBinColour = (
  stope: StopeLegendSource,
  config?: StopeLegendConfig | null,
): string | null => {
  if (!config || config.bins.length === 0) return null;

  const property = getStopeLegendPropertyOption(config.propertyId);
  const value = property.getValue(stope);
  if (value === null || value === undefined || `${value}` === '') return config.fallbackColor ?? FALLBACK_COLOR;

  if (config.binMode === 'value') {
    const valueText = `${roundValue(value)}`;
    const match = config.bins.find((bin) => `${bin.value}` === valueText);
    return match?.color ?? config.fallbackColor ?? FALLBACK_COLOR;
  }

  const numericValue = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numericValue)) return config.fallbackColor ?? FALLBACK_COLOR;

  const match = config.bins.find((bin, index) => {
    if (typeof bin.min !== 'number' || typeof bin.max !== 'number') return false;
    const lower = Math.min(bin.min, bin.max);
    const upper = Math.max(bin.min, bin.max);
    const isLast = index === config.bins.length - 1;
    return numericValue >= lower && (isLast ? numericValue <= upper : numericValue < upper);
  });

  return match?.color ?? config.fallbackColor ?? FALLBACK_COLOR;
};

export const getStopeLegendTitle = (config: StopeLegendConfig): string => {
  const property = getStopeLegendPropertyOption(config.propertyId);
  return `${config.name || 'Stope Legend'} - ${property.label}`;
};

export const getStopeLegendItems = (config: StopeLegendConfig) => config.bins.map((bin) => ({
  color: bin.color,
  label: bin.label,
}));

export const formatStopeLegendEditorValue = (
  propertyId: string,
  value: string | number | undefined,
): string => {
  if (value === undefined) return '';
  const property = getStopeLegendPropertyOption(propertyId);
  if (property.kind === 'date') return formatDate(value);
  return `${roundValue(value)}`;
};

export const parseStopeLegendEditorValue = (
  propertyId: string,
  value: string,
): string | number | undefined => {
  const property = getStopeLegendPropertyOption(propertyId);
  if (value.trim() === '') return undefined;

  if (property.kind === 'date') {
    try {
      return LocalDate.parse(value.trim()).toEpochDay();
    } catch {
      const numeric = Number(value);
      return Number.isFinite(numeric) ? numeric : undefined;
    }
  }

  if (property.kind === 'number' || property.kind === 'category') {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return numeric;
  }

  return value;
};

export const createEmptyStopeLegendBin = (config: StopeLegendConfig): StopeLegendBin => ({
  id: makeId(),
  label: '',
  color: DEFAULT_COLORS[config.bins.length % DEFAULT_COLORS.length],
  ...(config.binMode === 'range' ? { min: 0, max: 0 } : { value: '' }),
});
